import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import CommentsComponent from './CommentsComponent';
import { COMMENT_QUERY } from './queries';
import { DELETE_COMMENT } from './mutations';
import { COMMENT_DELETED } from './subscriptions';
import { useConditionalSubscription, useMutation } from 'features/common/security/hooks';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useAuthUserData, useToast, useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import { ADD_COMMENT } from './mutations';
import { COMMENT_ADDED } from './subscriptions';
import { emptyString } from 'constants/defaultValues';
import { Prompt } from 'react-router-dom';
import { DOCUMENT_HEADER_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';

const CommentsContainer = ({ documentId }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const client = useApolloClient();
  const { authUser } = useAuthUserData();
  const { globalReadOnly } = useContext(InsuranceDataContext);
  const showError = useSimpleError();
  const [comments, setComments] = useState([]);
  const [filter, setFilter] = useState();
  const [documentDetails, setDocumentDetails] = useState({});
  const [adding, setAdding] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const { data: documentDataHeader } = useQueryWithSimpleErrorHandling(DOCUMENT_HEADER_QUERY, {
    variables: { documentId }
  });

  const { loading } = useQueryWithSimpleErrorHandling(COMMENT_QUERY, {
    variables: { documentId },
    skip: !documentId,
    onCompleted: ({ commentData }) => {
      if (commentData) {
        const { comments, ...rest } = commentData;
        setDocumentDetails({ ...rest });
        setComments(prev => {
          const newComment = prev.find(x => !x.documentCommentId);
          if (!newComment) return [...comments];
          return [newComment, ...comments];
        });
      }
    }
  });

  useConditionalSubscription(COMMENT_DELETED, {
    onData: ({
      data: {
        data: { commentDeleted }
      }
    }) => {
      setProcessing(false);
      setComments(prevState => prevState.filter(comment => comment.documentCommentId !== commentDeleted.documentCommentId));
      client.cache.evict({ id: `Comment:{"documentCommentId":${commentDeleted.documentCommentId}}` });
      setIsDirty(false);
      addToast(t('Offer.Edit.Comments.CommentDeleted'), 'success', { toastId: 'commentDeletedToast' });
    }
  });

  const [deleteComment] = useMutation(DELETE_COMMENT, {
    onError: error => {
      showError(error);
      setProcessing(false);
    }
  });

  const handleDelete = documentCommentId => {
    if (documentCommentId) {
      setProcessing(true);
      deleteComment({
        variables: { input: { documentCommentId } }
      });
    } else {
      setComments(prevState => prevState.filter(x => !x.isNew));
      setAdding(false);
      setIsDirty(false);
    }
  };

  const [addComment] = useMutation(ADD_COMMENT, {
    onError: error => {
      showError(error);
      setProcessing(false);
    }
  });

  const handleAdd = () => {
    setAdding(true);
    const emptyComment = {
      documentCommentId: null,
      freeTxt: emptyString,
      internalTime: new Date(),
      userId: parseInt(authUser?.userId),
      userName: authUser?.userName,
      stateId: documentDataHeader?.documentHeader?.documentStateId,
      stateName: documentDataHeader?.documentHeader?.documentStateName,
      isNew: true
    };
    setComments(prevState => [emptyComment].concat(prevState));
    setIsDirty(true);
  };

  useConditionalSubscription(COMMENT_ADDED, {
    onData: ({
      data: {
        data: { commentAdded }
      }
    }) => {
      setAdding(false);
      setProcessing(false);
      setIsDirty(false);
      addToast(t('Offer.Edit.Comments.CommentAdded'), 'success', { toastId: 'commentAddedToast' });
      setComments(prevState =>
        commentAdded.commentData.comments.map(x =>
          prevState.some(y => y.documentCommentId === x.documentCommentId)
            ? prevState.find(y => y.documentCommentId === x.documentCommentId)
            : x
        )
      );
    }
  });

  const handleSave = comment => {
    setProcessing(true);
    addComment({
      variables: {
        input: {
          documentId: documentDetails?.documentId,
          stateId: documentDataHeader?.documentHeader?.documentStateId,
          freeTxt: comment.freeTxt
        }
      }
    });
    setIsDirty(true);
  };

  const filteredComments = useMemo(
    () => comments.filter(x => x.freeTxt.toLowerCase().includes((filter || '').toLowerCase()) || !x.documentCommentId),
    [comments, filter]
  );

  return (
    !loading && (
      <>
        {isDirty && <Prompt message={() => t('LeavingWithoutSaving')} />}
        <CommentsComponent
          comments={filteredComments}
          filter={filter}
          adding={adding}
          processing={processing}
          onAdd={handleAdd}
          onSave={handleSave}
          onDelete={handleDelete}
          onUpdateFilter={setFilter}
          readOnly={globalReadOnly({ controlName: 'Comments' })}
        />
      </>
    )
  );
};

CommentsContainer.propTypes = {
  documentId: PropTypes.number.isRequired
};

export default CommentsContainer;
